/**
 * AI Settings Page - AI 설정
 * URL: /admin/system
 */

import { useState, useEffect } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Button, Select, useToast } from '../components/ui';
import { systemSettingsApi, type SystemSettings } from '../api/system-settings';
import '../styles/adminPage.css';

const DEFAULT: SystemSettings = {
  defaultModel:        'amazon.nova-lite-v2:0',
  defaultLanguage:     'ko',
  defaultTemperature:  '0.7',
  topK:                5,
  similarityThreshold: 0.3,
};

export function SystemSettingsPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    systemSettingsApi.get()
      .then(setSettings)
      .catch(() => {/* 기본값 유지 */})
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof SystemSettings>(key: K, value: SystemSettings[K]) =>
    setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await systemSettingsApi.save(settings);
      showToast('success', 'AI 설정이 저장되었습니다.');
    } catch {
      showToast('error', '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const body = loading ? (
    <div className="loading">로딩 중...</div>
  ) : (
    <section>
      <div className="setting-section">
        <h3 className="setting-section-title">기본 설정</h3>
        <div className="setting-row">
          <label className="setting-label">기본 AI 모델</label>
          <Select className="setting-field" value={settings.defaultModel}
            onChange={e => set('defaultModel', e.target.value)}>
            <option value="amazon.nova-lite-v2:0">Amazon Nova Lite</option>
            <option value="amazon.nova-pro-v2:0">Amazon Nova Pro</option>
            <option value="anthropic.claude-haiku-4-5">Claude 3.5 Haiku</option>
            <option value="anthropic.claude-sonnet-4">Claude 4 Sonnet</option>
          </Select>
        </div>
        <div className="setting-row">
          <label className="setting-label">기본 응답 언어</label>
          <Select className="setting-field" value={settings.defaultLanguage}
            onChange={e => set('defaultLanguage', e.target.value)}>
            <option value="auto">자동 감지</option>
            <option value="ko">한국어</option>
            <option value="en">영어</option>
          </Select>
        </div>
        <div className="setting-row">
          <label className="setting-label">기본 Temperature</label>
          <Select className="setting-field" value={settings.defaultTemperature}
            onChange={e => set('defaultTemperature', e.target.value)}>
            <option value="0">0 - 정확함</option>
            <option value="0.3">0.3</option>
            <option value="0.5">0.5</option>
            <option value="0.7">0.7 - 균형</option>
            <option value="1.0">1.0 - 창의적</option>
          </Select>
        </div>
      </div>

      <div className="setting-section">
        <h3 className="setting-section-title">검색 설정</h3>
        <div className="setting-row">
          <label className="setting-label">기본 검색 개수 (Top K)</label>
          <div className="input setting-field setting-field--num">
            <input type="number" className="input-control"
              value={settings.topK} min={1} max={50}
              onChange={e => set('topK', Number(e.target.value))} />
          </div>
        </div>
        <div className="setting-row">
          <label className="setting-label">기본 유사도 임계값</label>
          <div className="input setting-field setting-field--num">
            <input type="number" className="input-control"
              value={settings.similarityThreshold} min={0} max={1} step={0.1}
              onChange={e => set('similarityThreshold', Number(e.target.value))} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '설정 저장'}
        </Button>
      </div>
    </section>
  );

  if (embedded) {
    return (
      <>
        <PanelTitle title="AI 설정" description="AI 모델 및 시스템 설정을 관리합니다." />
        {body}
      </>
    );
  }

  return (
    <>
      <Topbar title="AI 설정" description="AI 모델 및 시스템 설정을 관리합니다" />
      <div className="page-content">{body}</div>
    </>
  );
}

export default SystemSettingsPage;
